/**
 * Redaction of the values AngularJS fills into its error messages.
 *
 * Message shape:
 * - `[<module>:<code>] <template with values>`, then a link to `errors.angularjs.org` whose `pN` query parameters repeat those values.
 * - Some templates fill in whole objects or template expressions, which carry customer data.
 *
 * Drop this module when portal-v2 retires.
 */

import type { EventFilter, TelemetryEvent } from '@ringba/core/telemetry';

const LINK_PREFIX = 'https://errors.angularjs.org/';
const INJECTOR_PREFIX = '[$injector:';

// A shorter value carries no customer data and would match inside the template's own words.
const MIN_PARAM_LENGTH = 3;

/**
 * Replaces each value in an AngularJS error message with `{N}`, after its parameter index, and removes the link that repeats them.
 * `$injector` errors keep their values: they are module and provider names, which say which bundle failed to boot.
 * Never drops an event.
 *
 * Ordering: must run before any filter that rewrites an AngularJS message, which would leave the link in the stack.
 */
export function redactAngularErrorParams<T extends TelemetryEvent>(): EventFilter<T> {
  return (event) => {
    if (event.type !== 'error' || !event.error?.message?.includes(LINK_PREFIX)) return true;

    const original = event.error.message;
    const redacted = redactMessage(original);

    event.error.message = redacted;
    if (event.error.stack?.includes(original)) {
      // A replacer function, because a message may contain `$` sequences.
      event.error.stack = event.error.stack.replace(original, () => redacted);
    }
    return true;
  };
}

// --- Private ---

function redactMessage(message: string): string {
  const linkStart = message.indexOf(LINK_PREFIX);
  const text = message.slice(0, linkStart).trimEnd();
  if (text.startsWith(INJECTOR_PREFIX)) return text;

  const params = readParams(message.slice(linkStart).split(/\s/, 1)[0]);
  return params
    .map((value, index) => ({ value, index }))
    .filter(({ value }) => value.length >= MIN_PARAM_LENGTH)
    .sort((a, b) => b.value.length - a.value.length)
    .reduce((acc, { value, index }) => acc.split(value).join(`{${index}}`), text);
}

function readParams(link: string): string[] {
  const queryStart = link.indexOf('?');
  if (queryStart === -1) return [];

  const query = new URLSearchParams(link.slice(queryStart + 1));
  const params: string[] = [];
  for (let value = query.get('p0'); value !== null; value = query.get(`p${params.length}`)) {
    params.push(value);
  }
  return params;
}
